import { Point } from "@/components/atom/point/Point";
import { FormSubTitle } from "@/components/atom/title/form/FormSubTitle";
import { InspectRadioCategory } from "@/components/molecule/inspectRadioCategory/InspectRadioCategory";
import { InspectSegmentedCategory } from "@/components/molecule/inspectSegmentedCategory/InspectSegmentedCategory";
import InspectTables from "@/components/molecule/inspectTables/InspectTables";
import { MantineSelectableTable } from "@/components/molecule/mantineSelectableTable/MantineSelectableTable";
import FormTemplate from "@/components/template/form/FormTemplate";
import { COMMON_TEXT } from "@/constants/text";
import { useModal } from "@/context/ModalStackManager";
import { useInspectLogsFormFetch } from "@/fetch/form/useInspectLogsFormFetch";
import { useSpecificationFormFetch } from "@/fetch/form/useSpecificationFormFetch";
import { useUserFetch } from "@/fetch/user/useUserFetch";
import {
  onSubmitType,
  useInspectHandler,
} from "@/hooks/handler/useInspectHandler";
import { useWorkDataStore } from "@/store/work.store";
import { Pagination, Tabs, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  InspectionsGet200ResponseRowsInner,
  InspectionsGetRequestInspectionTypeEnum,
  SpcApiInspectionsInspectionIdSubmitPostRequest,
  SpcApiInspectionsPostRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useEffect, useMemo, useRef, useState } from "react";
import { InspectSpecificationAutoComplete } from "../autoComplete/inspectSpecification/inspectSpecification-autoComplete";

interface InspectionSpecificationsProps
  extends ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner {
  createdAt: string | undefined;
  isSaved: boolean;
  creatorUserId?: string | undefined;
}

const Inspect = Object.assign(
  {},
  {
    Template: FormTemplate,
    SubTitle: FormSubTitle,
    Point: Point,
    Table: MantineSelectableTable,
    SegmentedCategory: InspectSegmentedCategory,
    RadioCategory: InspectRadioCategory,
    Pagination: Pagination,
  }
);

export const InspectForm = () => {
  // Local types for form state (strongly typed instead of any)
  type InspectCellValue = string | number | boolean | null;
  interface InspectCell {
    value: InspectCellValue;
    isPassed?: boolean | null;
  }
  type InspectRecord = InspectCell[][];
  interface InspectFormValues {
    inspectionId: number;
    inspectionsInspectionIdSubmitPostRequest: {
      description?: string;
      record: InspectRecord;
    };
  }
  const { closeModal } = useModal();
  const [recordsByTab, setRecordsByTab] = useState<InspectRecord[]>([]);
  const [activeTab, setActiveTab] = useState<"first" | "middle" | "last">(
    "first"
  );
  const [touchedByTab, setTouchedByTab] = useState<boolean[]>([]);
  const [specificationId, setSpecificationId] = useState<number | undefined>(
    undefined
  );

  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));

  const { data: specification } = useSpecificationFormFetch();
  const { data: inspection } = useInspectLogsFormFetch();
  const {
    createMutateAsync,
    deleteMutateAsync,
    specificationPostData,
    resetSpecificationPostData,
    onSubmit: handlerSubmit,
  } = useInspectHandler();

  const token = localStorage.getItem("authToken");
  const { data: user } = useUserFetch({ token: token ?? "", enabled: !!token });
  const userData = user?.data;
  const name = userData?.name;

  const roles = userData?.permissions?.allow
    ?.map((role: string) => {
      if (role.includes("update")) return "UPDATE";
      else if (role.includes("create")) return "CREATE";
      else if (role.includes("delete")) return "DELETE";
      else if (role.includes("read")) return "READ";
      else return "READ";
    })
    .join(",");

  // 기존 진행중(IN_PROCESS) 검사 데이터가 있으면 우선 표시
  const inspectionData: InspectionsGet200ResponseRowsInner | undefined =
    inspection?.data?.rows?.find(
      (r: InspectionsGet200ResponseRowsInner) =>
        r?.inspectionType === InspectionsGetRequestInspectionTypeEnum.IN_PROCESS
    );
  const useExisting = Boolean(inspectionData);

  const subInspectionsData = specificationPostData?.subInspections ?? [];
  const subInspections = inspectionData?.subInspections ?? [];

  // 서브검사 목록(신규/기존 공통)
  const effectiveSubInspections = useExisting
    ? subInspections
    : subInspectionsData;
  const subByType = useMemo(() => {
    const map: Record<string, InspectionsGet200ResponseRowsInner | undefined> =
      {};
    (effectiveSubInspections || []).forEach((sub) => {
      map[sub?.subInspectionType as string] = sub;
    });
    return map;
  }, [effectiveSubInspections]);
  const presentEnumTypes = ["FIRST", "MIDDLE", "LAST"].filter(
    (k) => !!subByType[k]
  );
  const tabCount = presentEnumTypes.length || 1;
  const typeToEnum = (
    t: "first" | "middle" | "last" | undefined
  ): "FIRST" | "MIDDLE" | "LAST" | undefined =>
    t === "first"
      ? "FIRST"
      : t === "middle"
      ? "MIDDLE"
      : t === "last"
      ? "LAST"
      : undefined;
  const enumToType = (
    e: "FIRST" | "MIDDLE" | "LAST" | undefined
  ): "first" | "middle" | "last" | undefined =>
    e === "FIRST"
      ? "first"
      : e === "MIDDLE"
      ? "middle"
      : e === "LAST"
      ? "last"
      : undefined;
  const TYPE_INDEX: Record<string, number> = { first: 0, middle: 1, last: 2 };
  const activeIndex = TYPE_INDEX[activeTab] ?? 0;
  const effectiveSpecData = useMemo(() => {
    const enumValue = typeToEnum(activeTab);
    return (
      subByType[enumValue ?? ""]?.specificationData ||
      (useExisting
        ? inspectionData?.specificationData
        : specificationPostData?.specificationData)
    );
  }, [
    subByType,
    activeTab,
    useExisting,
    inspectionData?.specificationData,
    specificationPostData?.specificationData,
  ]);

  // 현재 탭 서브검사의 record는 각 패널 내부에서 사용하며, 여기서는 별도 필터링 로직을 두지 않음

  const recordForm = useForm<InspectFormValues>({
    initialValues: {
      inspectionId: 0,
      inspectionsInspectionIdSubmitPostRequest: {
        description: undefined,
        record: [],
      },
    },
  });
  const initialRecordRef = useRef<InspectRecord>([]);
  // 신규 생성 흐름에서 탭별 값 유지를 위한 타입 기반 캐시(FIRST/MIDDLE/LAST)
  const cachedByTypeRef = useRef<Record<string, InspectRecord>>({});

  // 신규 생성 시 각 subInspectionType별 기본 레코드를 미리 생성해 두어 탭 전환 시 초기화로 사라지지 않게 함
  useEffect(() => {
    if (useExisting) return;
    ["FIRST", "MIDDLE", "LAST"].forEach((enumType) => {
      const sub = subByType[enumType];
      if (!cachedByTypeRef.current[enumType]) {
        const criteria = sub?.specificationData?.criteria ?? [];
        const defaults: InspectRecord = criteria.map((criteria) =>
          Array.from({ length: criteria?.sampleNum ?? 1 }).map(() => {
            let value: InspectCellValue = null;
            let isPassed: boolean | null | undefined = undefined;
            switch (criteria?.type) {
              case "boolean":
                value = false;
                break;
              case "number":
                value = null;
                break;
              case "select":
                value = null;
                break;
              case "description":
                value = null;
                // description은 체크박스로만 합격 처리
                isPassed = false;
                break;
              default:
                value = null;
                break;
            }
            return { value, isPassed } as InspectCell;
          })
        );
        cachedByTypeRef.current[enumType] = defaults;
      }
    });
  }, [subByType, useExisting]);

  useEffect(() => {
    // 고정 3개(first/middle/last) 슬롯 유지
    setRecordsByTab((prev) =>
      Array.from({ length: 3 }, (_, i) => prev?.[i] ?? [])
    );
    setTouchedByTab((prev) => {
      const next = Array.from({ length: 3 }, (_, i) => prev?.[i] ?? false);
      return next;
    });
  }, []);

  // activeTab이 현재 탭 목록과 불일치 시 첫 번째 subInspectionType으로 보정
  useEffect(() => {
    const exists = ["FIRST", "MIDDLE", "LAST"]
      .filter((k) => !!subByType[k])
      .map((e) => enumToType(e as "FIRST" | "MIDDLE" | "LAST"))
      .includes(activeTab);
    if (!exists) {
      const firstEnum = ["FIRST", "MIDDLE", "LAST"].find((k) => !!subByType[k]);
      setActiveTab(
        enumToType(firstEnum as "FIRST" | "MIDDLE" | "LAST") ?? "first"
      );
    }
  }, [subByType, activeTab]);

  // activeTab 또는 스펙 데이터 변경 시 해당 탭의 기록으로 폼 초기화
  useEffect(() => {
    if (!effectiveSpecData?.criteria) return;
    // 기존 데이터가 있으면 해당 탭의 기록을 우선 적용
    const enumValue = typeToEnum(activeTab);
    const existingRecordForTab: InspectRecord | undefined = useExisting
      ? (subByType[enumValue ?? ""]?.record as unknown as InspectRecord)
      : undefined;
    // 신규 생성 흐름에서는 탭별 캐시를 우선 사용하여 값 유지
    const enumActive = typeToEnum(activeTab);
    const cachedRecordForTabByType: InspectRecord | undefined = !useExisting
      ? cachedByTypeRef.current[enumActive ?? ""]
      : undefined;
    const cachedRecordForTab: InspectRecord | undefined = !useExisting
      ? recordsByTab?.[activeIndex]
      : undefined;

    const recordInit: InspectRecord =
      existingRecordForTab && existingRecordForTab.length
        ? existingRecordForTab
        : cachedRecordForTabByType && cachedRecordForTabByType.length
        ? cachedRecordForTabByType
        : cachedRecordForTab && cachedRecordForTab.length
        ? cachedRecordForTab
        : effectiveSpecData.criteria.map((criteria) =>
            Array.from({ length: criteria.sampleNum ?? 1 }).map(
              (): InspectCell => {
                let defaultValue: InspectCellValue;
                let isPassed: boolean | null = null;
                switch (criteria.type) {
                  case "boolean":
                    defaultValue = false;
                    break;
                  case "number":
                    defaultValue = null;
                    break;
                  case "select":
                    defaultValue = null;
                    break;
                  case "description":
                  default:
                    defaultValue = null;
                    // description은 체크박스로만 합격 처리
                    isPassed = false;
                    break;
                }
                return { value: defaultValue, isPassed } as InspectCell;
              }
            )
          );
    const activeSubId = subByType[enumValue ?? ""]?.id as number | undefined;
    recordForm.setValues({
      inspectionId:
        activeSubId ??
        (useExisting
          ? inspectionData?.id ?? 0
          : specificationPostData?.id ?? 0),
      inspectionsInspectionIdSubmitPostRequest: {
        description: undefined,
        record: recordInit,
      },
    });
    initialRecordRef.current = recordInit.map((r) => r.map((c) => ({ ...c })));
  }, [
    effectiveSpecData,
    specificationPostData?.id,
    inspectionData?.id,
    activeIndex,
    useExisting,
    subByType,
    recordsByTab,
  ]);

  const handleOnSubmit = () => {
    const inspectionIdForSubmit = recordForm.values.inspectionId ?? 0;
    const current =
      recordForm.values.inspectionsInspectionIdSubmitPostRequest.record;
    const base = initialRecordRef.current ?? [];
    const enumValue = typeToEnum(activeTab);
    const hasPrevious = useExisting
      ? Boolean(subByType[enumValue ?? ""]?.record?.length)
      : Boolean(
          (
            inspection?.data?.rows?.[0] as
              | InspectionsGet200ResponseRowsInner
              | undefined
          )?.record?.length
        );
    const criteriaTypes = effectiveSpecData?.criteria?.map(
      (criteria) => criteria.type
    );

    handlerSubmit({
      inspectionId: inspectionIdForSubmit,
      record: current,
      baseRecord: base,
      description:
        recordForm.values.inspectionsInspectionIdSubmitPostRequest.description,
      hasPrevious,
      criteriaTypes,
    } as onSubmitType);
    // 이후 기준 갱신 및 탭 unlock
    initialRecordRef.current = current.map((r) => r.map((c) => ({ ...c })));
    setTouchedByTab((prev) => {
      const next = [...prev];
      next[activeIndex] = false;
      return next;
    });
  };

  const nextPayload: SpcApiInspectionsPostRequest = {
    inspectionsGetRequest: {
      itemCode: workData.work?.item?.code ?? "",
      lotId: workData?.work?.productionPlan?.lastLotId,
      inspectionType: InspectionsGetRequestInspectionTypeEnum.IN_PROCESS,
      specificationId: specificationId ?? 0,
      productionPlanId: workData.work?.productionPlanId,
      workId: workData.work?.id,
    },
  };
  const [inspectionId, setInspectionId] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (useExisting) return; // 기존 IN_PROCESS 데이터가 있으면 신규 생성 흐름 중단
    let cancelled = false;
    const run = async () => {
      // 선택 해제: 기존 생성건 삭제
      if (specificationId == null) {
        // UI/상태 초기화 (이전 스펙 데이터 제거)
        resetSpecificationPostData();
        setRecordsByTab([]);
        setActiveTab("first");
        if (inspectionId) {
          try {
            await deleteMutateAsync({ inspectionId });
          } finally {
            if (!cancelled) setInspectionId(undefined);
          }
        }
        return;
      }

      // 값 변경: 기존 생성건 삭제 후 새로 생성
      if (inspectionId) {
        await deleteMutateAsync({ inspectionId });
      }
      // 이전 스펙 데이터 제거 후 새 스펙으로 교체될 수 있도록 초기화
      resetSpecificationPostData();
      setRecordsByTab([]);
      setActiveTab("first");
      const res = (await createMutateAsync(nextPayload)) as unknown as {
        data?: { id?: number };
        id?: number;
      };
      const newId = res?.data?.id ?? res?.id;
      if (!cancelled) setInspectionId(newId);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [specificationId, useExisting]);

  return !useExisting && specification?.data.rows?.length === 0 ? (
    <Text>{COMMON_TEXT.DATA_EMPTY.NO_INSPECT}</Text>
  ) : (
    <Inspect.Template
      onCancel={() => closeModal({})}
      onSubmit={handleOnSubmit}
      isButtonDisabled={!roles?.includes("UPDATE")}
    >
      {!useExisting && (
        <InspectSpecificationAutoComplete
          inspectionType={InspectionsGetRequestInspectionTypeEnum.IN_PROCESS}
          itemCode={workData.work?.item?.code}
          routingCode={workData.work?.routingCode}
          onChange={(e, option) => {
            setSpecificationId(option?.id);
          }}
          label={"검사 기준 선택"}
        />
      )}
      <Tabs
        variant="pills"
        mt="md"
        value={activeTab}
        keepMounted
        onTabChange={(v) => {
          const next = String(v ?? "first") as "first" | "middle" | "last";
          const isDirty = touchedByTab[activeIndex] === true;
          // 신규 생성 흐름일 때 현재 탭 값을 먼저 캐시에 저장
          if (!useExisting) {
            const current =
              recordForm.values.inspectionsInspectionIdSubmitPostRequest.record;
            const currentEnum = typeToEnum(activeTab) ?? "";
            cachedByTypeRef.current[currentEnum] = current.map((r) =>
              r.map((c) => ({ ...c }))
            );
            setRecordsByTab((prev) => {
              const nextCache = [...prev];
              nextCache[activeIndex] = current.map((r) =>
                r.map((c) => ({ ...c }))
              );
              return nextCache;
            });
          }
          // 다음 탭 레코드를 먼저 주입해 깜빡임과 초기화 손실 방지
          const nextEnum = typeToEnum(next);
          const nextSub = subByType[nextEnum ?? ""];
          const nextSpec =
            nextSub?.specificationData ||
            (useExisting
              ? inspectionData?.specificationData
              : specificationPostData?.specificationData);
          const cachedNext = !useExisting
            ? cachedByTypeRef.current[nextEnum ?? ""]
            : undefined;
          let nextRecordInit: InspectRecord | undefined = useExisting
            ? (nextSub?.record as unknown as InspectRecord)
            : cachedNext;
          if (!nextRecordInit && nextSpec?.criteria) {
            nextRecordInit = nextSpec.criteria.map((criteria) =>
              Array.from({ length: criteria.sampleNum ?? 1 }).map(() => {
                let defaultValue: string | number | boolean | null;
                let isPassed: boolean | null = null;
                switch (criteria.type) {
                  case "boolean":
                    defaultValue = false;
                    break;
                  case "number":
                    defaultValue = null;
                    break;
                  case "select":
                    defaultValue = null;
                    break;
                  case "description":
                  default:
                    defaultValue = null;
                    // description은 체크박스로만 합격 처리
                    isPassed = false;
                    break;
                }
                return { value: defaultValue, isPassed };
              })
            );
          }
          if (nextRecordInit) {
            const nextInspectionId =
              (nextSub?.id as number | undefined) ??
              (useExisting
                ? inspectionData?.id ?? 0
                : specificationPostData?.id ?? 0);
            recordForm.setValues({
              inspectionId: nextInspectionId,
              inspectionsInspectionIdSubmitPostRequest: {
                description: undefined,
                record: nextRecordInit,
              },
            });
            initialRecordRef.current = nextRecordInit.map((r) =>
              r.map((c) => ({ ...c }))
            );
          }
          if (isDirty && next !== activeTab) return; // 변경사항 있으면 탭 전환 금지
          setActiveTab(next);
        }}
      >
        {tabCount > 1 && (
          <Tabs.List>
            {["first", "middle", "last"].map((type) => {
              const label =
                type === "first"
                  ? COMMON_TEXT.INSPECT.INITIAL_PRODUCT
                  : type === "middle"
                  ? COMMON_TEXT.INSPECT.INTERMEDIATE_PRODUCT
                  : COMMON_TEXT.INSPECT.FINAL_PRODUCT;
              return (
                <Tabs.Tab
                  key={type}
                  value={type}
                  disabled={touchedByTab[activeIndex] && type !== activeTab}
                >
                  {label}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        )}
        {["first", "middle", "last"].map((type) => {
          const enumType = typeToEnum(type as "first" | "middle" | "last");
          const sub = subByType[enumType ?? ""];
          return (
            <Tabs.Panel key={type} value={type}>
              <InspectTables
                criteriaList={
                  sub?.specificationData?.criteria ??
                  effectiveSpecData?.criteria
                }
                recordForm={{
                  values:
                    recordForm.values as SpcApiInspectionsInspectionIdSubmitPostRequest,
                  setFieldValue: (
                    path: string,
                    value: SpcApiInspectionsInspectionIdSubmitPostRequest
                  ) => recordForm.setFieldValue(path, value),
                }}
                acceptanceStatus={true}
                onAnyChange={(nextRecord) => {
                  setTouchedByTab((prev) => {
                    const next = [...prev];
                    next[activeIndex] = true;
                    return next;
                  });
                  // 신규 생성 흐름일 때, 현재 탭 입력값을 캐시에 저장하여 탭 이동 후에도 유지
                  if (!useExisting) {
                    const current = nextRecord;
                    const currentEnum = typeToEnum(activeTab) ?? "";
                    cachedByTypeRef.current[currentEnum] = current.map((r) =>
                      r.map((c) => ({ ...c }))
                    );
                    setRecordsByTab((prev) => {
                      const next = [...prev];
                      next[activeIndex] = current.map((r) =>
                        r.map((c) => ({ ...c }))
                      );
                      return next;
                    });
                  }
                }}
              />
            </Tabs.Panel>
          );
        })}
      </Tabs>
    </Inspect.Template>
  );
};
